import { StorageService } from './storage.service';
import {
  catchError,
  map,
  Observable,
  Subscription,
  tap,
  throwError,
} from 'rxjs';
import { FileUpload, FileUploadChunk } from '../models/file-upload.model';
import {
  creatFileUploadChunkState,
  creatFileUploadChunkStateFromEvent,
  FileUploadChunkState,
  FileUploadState,
} from '../models/file-upload-state.model';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StorageStateService } from './storage-state.service';

@Injectable({
  providedIn: 'root',
})
export class StorageUploadStateService {
  private readonly storageStateService = inject(StorageStateService);

  public readonly isFileUploadVisible = signal<boolean>(false);

  public readonly file = signal<File>(null);
  public readonly fileUpload = signal<FileUpload>(null);
  public readonly fileUploadState = signal<FileUploadState>(FileUploadState.READY);
  public readonly fileUploadChunks = signal<FileUploadChunkState[]>(null);
  public readonly fileUploadChunksSubscription = signal<Subscription>(null);

  public readonly fileUploadChunksProgress = computed(() => {
    const fileUploadChunks = this.fileUploadChunks();
    if (fileUploadChunks) {
      const bytesUploaded = fileUploadChunks
        .map((e) => e.bytesUploaded)
        .reduce((value, accumulator) => accumulator + value, 0);
      const bytesTotal = fileUploadChunks
        .map((e) => e.bytesTotal)
        .reduce((value, accumulator) => accumulator + value, 0);
      return Math.round((bytesUploaded / bytesTotal) * 100);
    } else {
      return 0;
    }
  });

  public constructor(
    private readonly storageService: StorageService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public startUploading(folderId: string, file: File): void {
    this.isFileUploadVisible.set(true);
    this.storageStateService.folderId.set(folderId);
    this.file.set(file);
    this.fileUploadState.set(FileUploadState.PENDING);

    this.storageService
      .createFile(folderId, file.size)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((fileUpload) => this.fileUpload.set(fileUpload)),
        tap((fileUpload) => {
          this.fileUploadState.set(FileUploadState.UPLOADING);
          this.initializeFileUploadChunks(fileUpload);
          this.initializeFileUploadChunksUpload(file, fileUpload);
        }),
        catchError((error) => {
          this.fileUploadState.set(FileUploadState.ERROR);
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  public pauseUploading(): void {
    this.fileUploadState.set(FileUploadState.PAUSED);
    this.fileUploadChunksSubscription().unsubscribe();
    this.fileUploadChunksSubscription.set(null);
  }

  public resumeUploading(): void {
    this.fileUploadState.set(FileUploadState.UPLOADING);
    const fileUploadChunk = this.fileUploadChunks().find((e) => !e.completed);
    this.initializeFileUploadChunkUpload(this.file(), fileUploadChunk);
  }

  public cancelUploading(): void {
    const fileUploadChunk = this.fileUploadChunks().find((e) => !e.completed);
    this.updateFileUploadChunkStateWithError(fileUploadChunk, '');
    this.fileUploadState.set(FileUploadState.ERROR);
  }

  public closeFileUpload() {
    this.isFileUploadVisible.set(false);
  }

  private initializeFileUploadChunks(fileUpload: FileUpload): void {
    this.fileUploadChunks.set(
      fileUpload.chunks.map((fileUploadChunk) =>
        creatFileUploadChunkState(fileUploadChunk),
      ),
    );
  }

  private initializeFileUploadChunksUpload(
    file: File,
    fileUpload: FileUpload,
  ): void {
    this.initializeFileUploadChunkUpload(file, fileUpload.chunks[0]);
  }

  private initializeFileUploadChunkUpload(
    file: File,
    fileUploadChunk: FileUploadChunk,
  ): void {
    const subscription = this.storageService
      .uploadFile(
        fileUploadChunk,
        file.slice(fileUploadChunk.start, fileUploadChunk.end, file.type),
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((fileUploadEvent) =>
          creatFileUploadChunkStateFromEvent(fileUploadChunk, fileUploadEvent),
        ),
        tap((fileUploadChunkState) =>
          this.updateFileUploadChunkState(fileUploadChunkState),
        ),
        tap((fileUploadChunkState) => {
          if (fileUploadChunkState.completed) {
            const fileUploadChunks = this.fileUploadChunks();
            const fileUploadChunkIndex = fileUploadChunks.findIndex(
              (e) => e.position === fileUploadChunk.position,
            );
            if (fileUploadChunkIndex < fileUploadChunks.length - 1) {
              this.initializeFileUploadChunkUpload(
                file,
                fileUploadChunks[fileUploadChunkIndex + 1],
              );
            }
          }
        }),
        catchError((error) =>
          this.updateFileUploadChunkStateWithError(fileUploadChunk, error),
        ),
      )
      .subscribe();

    this.fileUploadChunksSubscription.set(subscription);
  }

  private updateFileUploadChunkState(
    fileUploadChunkState: FileUploadChunkState,
  ): void {
    this.fileUploadChunks.update((value) =>
      value.map((e) =>
        this.equalsFileUploadChunks(e, fileUploadChunkState)
          ? fileUploadChunkState
          : e,
      ),
    );
    this.finalizeFileUploadIfCompleted();
  }

  private updateFileUploadChunkStateWithError(
    fileUploadChunk: FileUploadChunk,
    error: unknown,
  ): Observable<unknown> {
    this.fileUploadState.set(FileUploadState.ERROR);
    this.fileUploadChunks.update((value) =>
      value.map((fileUploadChunkState) =>
        this.equalsFileUploadChunks(fileUploadChunkState, fileUploadChunk)
          ? { ...fileUploadChunkState, error: true }
          : fileUploadChunkState,
      ),
    );
    return throwError(() => error);
  }

  private finalizeFileUploadIfCompleted(): void {
    const fileUploadChunks = this.fileUploadChunks();
    const fileUploadCompleted = fileUploadChunks.every((e) => e.completed);
    if (fileUploadCompleted) {
      this.finalizeFileUpload();
    }
  }

  private finalizeFileUpload(): void {
    const folderId = this.storageStateService.folderId();
    const file = this.file();
    const fileUpload = this.fileUpload();

    this.fileUploadState.set(FileUploadState.FINALIZING);
    this.storageService
      .createFileCompleted(folderId, {
        id: fileUpload.id,
        name: file.name,
        type: file.type,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.fileUploadState.set(FileUploadState.COMPLETED);
          this.storageStateService.refreshFiles();
        }),
        catchError((error) => {
          this.fileUploadState.set(FileUploadState.ERROR);
          return throwError(() => error);
        }),
      )
      .subscribe();
  }

  private equalsFileUploadChunks(
    a: FileUploadChunk | FileUploadChunkState,
    b: FileUploadChunk | FileUploadChunkState,
  ): boolean {
    return a.position === b.position;
  }
}
